import React from 'react';
import { Search, CheckCircle, XCircle, Loader2, ArrowUp, ArrowDown } from 'lucide-react';

interface HistoryFilterBarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filterExecutionStatus: string;
  onFilterExecutionStatusChange: (value: string) => void;
  filterDateRange: string;
  onFilterDateRangeChange: (value: string) => void;
  sortBy: string;
  onSortByChange: (value: string) => void;
  sortOrder: 'asc' | 'desc';
  onSortOrderToggle: () => void;
}

export const HistoryFilterBar: React.FC<HistoryFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  filterExecutionStatus,
  onFilterExecutionStatusChange,
  filterDateRange,
  onFilterDateRangeChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderToggle,
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-6">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search repositories..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Status pills */}
        <div className="flex items-center gap-0.5 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
          {(
            [
              {
                value: 'all',
                label: 'All',
                icon: null,
                active: 'bg-white dark:bg-gray-600 text-gray-800 dark:text-gray-100 shadow-sm',
              },
              {
                value: 'analyzing',
                label: 'Analyzing',
                icon: Loader2,
                active: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400',
              },
              {
                value: 'completed',
                label: 'Completed',
                icon: CheckCircle,
                active: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
              },
              {
                value: 'failed',
                label: 'Failed',
                icon: XCircle,
                active: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
              },
            ] as const
          ).map(({ value, label, icon: Icon, active }) => (
            <button
              key={value}
              onClick={() => onFilterExecutionStatusChange(value)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                filterExecutionStatus === value
                  ? active
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {Icon && (
                <Icon
                  className={`w-3.5 h-3.5 ${value === 'analyzing' && filterExecutionStatus === 'analyzing' ? 'animate-spin' : ''}`}
                />
              )}
              {label}
            </button>
          ))}
        </div>

        {/* Date range pills */}
        <div className="flex items-center gap-0.5 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
          {(
            [
              { value: 'all', label: 'All time' },
              { value: '7', label: '7d' },
              { value: '30', label: '30d' },
              { value: '90', label: '90d' },
            ] as const
          ).map(({ value, label }) => (
            <button
              key={value}
              onClick={() => onFilterDateRangeChange(value)}
              title={value === 'all' ? 'All time' : `Last ${value} days`}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                filterDateRange === value
                  ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-gray-200 dark:bg-gray-600" aria-hidden="true" />

        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => onSortByChange(e.target.value)}
          className="w-auto px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
          aria-label="Sort by"
        >
          <option value="lastAnalysis">Last analysis</option>
          <option value="name">Repository name</option>
          <option value="status">Status</option>
        </select>
        <button
          onClick={onSortOrderToggle}
          className="p-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-400 dark:hover:border-teal-500 transition-colors"
          aria-label={
            sortOrder === 'asc'
              ? 'Sort ascending — click for descending'
              : 'Sort descending — click for ascending'
          }
          title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        >
          {sortOrder === 'asc' ? (
            <ArrowUp className="w-3.5 h-3.5" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5" />
          )}
        </button>

        {/* Clear filters */}
        {(filterExecutionStatus !== 'all' || filterDateRange !== 'all') && (
          <button
            onClick={() => {
              onFilterExecutionStatusChange('all');
              onFilterDateRangeChange('all');
            }}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            title="Clear all filters"
          >
            <XCircle className="w-3.5 h-3.5" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};
